import { useState, useEffect } from "react";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import ShadowContainer from "../../components/common/ShadowContainer";
import { API_BASE } from "../../utils/api";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";

function AddSubCategory() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [subCat, setSubCat] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [image, setImage] = useState(null);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [enableMultiLang, setEnableMultiLang] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/languages/availableLanguages`)
      .then((res) => res.json())
      .then((data) => setLanguages(data.filter((l) => l.code !== "en")))
      .catch(() => toast.error("Failed to load languages"));
  }, []);

  const navigate = useNavigate();
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        //const response = await axios.get(`${API_BASE}/api/categories/all`);
        const response = await api.get("/categories/all"); //newly added for configured Axios instance
        const normalizedCategories = response.data.map((cat) => ({
          ...cat,
          _i18n: cat.name || {},
          name: typeof cat.name === "string" ? cat.name : cat.name?.en || "",
        }));

        setCategories(normalizedCategories);
      } catch (err) {
        toast.error("Failed to load categories");
      }
    };

    fetchCategories();
  }, []);

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const token = localStorage.getItem("token");
  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setErrors({ ...errors, image: "Only JPG and PNG files are allowed." });
      setImage(null);
      setImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, image: "File size must be less than 5MB." });
      setImage(null);
      setImagePreview(null);
      return;
    }

    setErrors({ ...errors, image: "" });
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCategoryChange = (e) => setCategory(e.target.value);
  const handleSubCatChange = (lang, value) => {
    setSubCat((prev) => ({
      ...prev,
      [lang]: capitalizeWords(value),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!category) {
      newErrors.category = "Please select a category.";
    }
    if (!subCat.en?.trim()) {
      newErrors.subCat = "Please enter subcategory name (English).";
    }

    if (!image) {
      newErrors.image = "Please upload an image.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!validateForm()) {
      setIsSubmitted(false);
      return;
    }

    try {
      // const checkResponse = await axios.get(`${API_BASE}/api/subcategory/check?category=${category}&subCat=${subCat}`);
      const checkResponse = await api.get(
        `/subcategory/check?category=${category}&subCat=${subCat}`,
      );

      const checkData = checkResponse.data;

      if (checkData.exists) {
        toast.warning("Subcategory exists in the selected category!");
        setIsSubmitted(false);
        return;
      }

      const formData = new FormData();
      formData.append("category", category);
      formData.append("subCat", JSON.stringify(subCat));

      if (image) formData.append("image", image);

      // const response = await axios.post(`${API_BASE}/api/subcategory/add-subcategory`, formData, {
      //     headers: { 'Content-Type': 'multipart/form-data' }
      const response = await api.post(
        "/subcategory/add-subcategory",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 201) {
        toast.success(
          "Subcategory added!Please enable to see in customer UI from edit screen",
        );
        setCategory("");
        setSubCat("");
        setImage(null);
        setImagePreview(null);
        setErrors({});
        setFileInputKey(Date.now());
      } else {
        toast.error("Failed to add subcategory");
      }
    } catch (err) {
      console.error("Error submitting subcategory:", err);
      toast.error("Failed to add subcategory");
    } finally {
      setIsSubmitted(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3">
      <PageHeader
        showBackButton={true}
        backTo="/subcategories"
        title="Add Subcategory"
        subtitle="Create a new subcategory for your menu"
      />

      <ShadowContainer maxWidth="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={category}
              onChange={handleCategoryChange}
              disabled={isSubmitted}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white ${
                errors.category ? "border-red-500 bg-red-50" : "border-gray-300"
              } ${isSubmitted ? "bg-gray-100 cursor-not-allowed" : ""}`}
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category}</p>
            )}
          </div>

          {/* Subcategory Name (EN) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategory Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subCat.en || ""}
              onChange={(e) => handleSubCatChange("en", e.target.value)}
              disabled={isSubmitted}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${
                errors.subCat ? "border-red-500 bg-red-50" : "border-gray-300"
              } ${isSubmitted ? "bg-gray-100 cursor-not-allowed" : ""}`}
              placeholder="Enter subcategory name"
            />
            {errors.subCat && (
              <p className="mt-1 text-sm text-red-600">{errors.subCat}</p>
            )}
          </div>

          {/* Enable Multi-language */}
          {languages && languages.length > 0 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enableMultiLang"
                checked={enableMultiLang}
                onChange={(e) => {
                  setEnableMultiLang(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedLang("");
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label
                htmlFor="enableMultiLang"
                className="ml-2 text-sm font-medium text-gray-700"
              >
                Enable multi-language
              </label>
            </div>
          )}

          {/* Language Selection */}
          {enableMultiLang && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Select Language
              </p>
              <div className="space-y-2">
                {languages.map((lang) => (
                  <label
                    key={lang.code}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="language"
                      value={lang.code}
                      checked={selectedLang === lang.code}
                      onChange={(e) => {
                        const langCode = e.target.value;
                        setSelectedLang(langCode);
                        if (!subCat[langCode]) {
                          setSubCat((prev) => ({ ...prev, [langCode]: "" }));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {lang.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Multi-language Field */}
          {enableMultiLang && selectedLang && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subcategory Name ({selectedLang.toUpperCase()})
              </label>
              <input
                type="text"
                value={subCat[selectedLang] || ""}
                onChange={(e) =>
                  handleSubCatChange(selectedLang, e.target.value)
                }
                dir={
                  languages.find((l) => l.code === selectedLang)?.direction ||
                  "ltr"
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                placeholder={`Enter subcategory name in ${selectedLang}`}
              />
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategory Image <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <input
                key={fileInputKey}
                type="file"
                accept="image/jpeg, image/png"
                onChange={handleImageChange}
                disabled={isSubmitted}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              <p className="mt-1 text-xs text-gray-500">
                JPEG or PNG (max 5MB)
              </p>
              {errors.image && (
                <p className="mt-1 text-sm text-red-600">{errors.image}</p>
              )}
            </div>
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-40 h-40 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSubmitted || !!errors.image}
              className={`px-6 py-2.5 rounded-lg font-medium text-white transition-colors duration-200 ${
                isSubmitted || !!errors.image
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitted ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </span>
              ) : (
                "Save Subcategory"
              )}
            </button>
          </div>
        </form>
      </ShadowContainer>
    </div>
  );
}

export default AddSubCategory;
